import React, { useEffect, useState } from "react";
import { Calculator, PiggyBank, Landmark, Gauge, ArrowRight, ShieldCheck } from "lucide-react";
import api, { inr, getPublicSettings } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSEO } from "@/hooks/useSEO";

function BrandCTA({ label, url, accent = "emerald" }) {
  if (!label || !url) return null;
  const cls = accent === "indigo" ? "bg-indigo-600 hover:bg-indigo-700" : "bg-teal-600 hover:bg-teal-700";
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={`calc-cta-${accent}`} className="block mt-5">
      <Button className={`w-full ${cls} text-white font-bold h-12 rounded-xl`}>
        {label} <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </a>
  );
}

function Field({ label, value, onChange, suffix }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-600">{label}</label>
      <div className="relative mt-1.5">
        <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="h-11 font-mono-num pr-12" />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{suffix}</span>}
      </div>
    </div>
  );
}

function EMICalculator({ cta }) {
  const [P, setP] = useState(500000);
  const [rate, setRate] = useState(11);
  const [years, setYears] = useState(5);
  const n = years * 12;
  const r = rate / 12 / 100;
  const emi = r === 0 ? P / n : (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const total = emi * n;
  const interest = total - P;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="emi-calculator">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Landmark className="w-5 h-5" /></div>
        <h2 className="font-display font-bold text-xl text-[#0B223D]">Loan EMI Calculator</h2>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Loan amount" value={P} onChange={setP} suffix="₹" />
        <Field label="Interest rate (p.a.)" value={rate} onChange={setRate} suffix="%" />
        <Field label="Tenure" value={years} onChange={setYears} suffix="yrs" />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-6">
        {[["Monthly EMI", emi, "emerald"], ["Total interest", interest, "amber"], ["Total payable", total, "navy"]].map(([l, v, c]) => (
          <div key={l} className={`rounded-xl p-4 ${c === "emerald" ? "bg-teal-50" : c === "amber" ? "bg-amber-50" : "bg-slate-50"}`}>
            <p className="text-xs font-semibold uppercase text-slate-400">{l}</p>
            <p className="font-mono-num font-extrabold text-lg text-[#0B223D] mt-1">{inr(Math.round(v))}</p>
          </div>
        ))}
      </div>
      <BrandCTA label={cta?.emi_cta_label} url={cta?.emi_cta_url} accent="indigo" />
    </div>
  );
}

function SIPCalculator({ cta }) {
  const [mode, setMode] = useState("sip");
  const [amt, setAmt] = useState(5000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);
  const n = years * 12;
  const i = rate / 12 / 100;
  let invested, fv;
  if (mode === "sip") {
    fv = i === 0 ? amt * n : amt * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
    invested = amt * n;
  } else {
    fv = amt * Math.pow(1 + rate / 100, years);
    invested = amt;
  }
  const gain = fv - invested;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="sip-calculator">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center"><PiggyBank className="w-5 h-5" /></div>
        <h2 className="font-display font-bold text-xl text-[#0B223D]">SIP Calculator</h2>
      </div>
      <div className="flex gap-2 mb-4" data-testid="sip-mode-toggle">
        {[["sip", "SIP (Monthly)"], ["lumpsum", "Lumpsum (One-time)"]].map(([m, l]) => (
          <button key={m} type="button" onClick={() => { setMode(m); setAmt(m === "sip" ? 5000 : 100000); }} data-testid={`sip-mode-${m}`}
            className={`flex-1 h-10 rounded-xl text-sm font-bold transition-colors ${mode === m ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{l}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label={mode === "sip" ? "Monthly investment" : "One-time investment"} value={amt} onChange={setAmt} suffix="₹" />
        <Field label="Expected return (p.a.)" value={rate} onChange={setRate} suffix="%" />
        <Field label="Period" value={years} onChange={setYears} suffix="yrs" />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-6">
        {[["Invested", invested, "slate"], ["Est. returns", gain, "amber"], ["Total value", fv, "emerald"]].map(([l, v, c]) => (
          <div key={l} className={`rounded-xl p-4 ${c === "emerald" ? "bg-teal-50" : c === "amber" ? "bg-amber-50" : "bg-slate-50"}`}>
            <p className="text-xs font-semibold uppercase text-slate-400">{l}</p>
            <p className="font-mono-num font-extrabold text-lg text-[#0B223D] mt-1" data-testid={`sip-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{inr(Math.round(v))}</p>
          </div>
        ))}
      </div>
      <BrandCTA label={cta?.sip_cta_label} url={cta?.sip_cta_url} accent="emerald" />
    </div>
  );
}

function CibilCheck() {
  const [cfg, setCfg] = useState({});
  useEffect(() => { getPublicSettings().then(setCfg).catch(() => {}); }, []);
  const title = cfg.cibil_title || "Free CIBIL Score Check";
  const desc = cfg.cibil_desc || "Check your credit score instantly — a soft enquiry that does not impact your score. Know where you stand before applying for loans or credit cards.";
  const btnLabel = cfg.cibil_btn_label || "Check my CIBIL score";
  const link = cfg.cibil_link || "";
  return (
    <div className="bg-brand-gradient text-white rounded-2xl p-6 sm:p-7" data-testid="cibil-check">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><Gauge className="w-5 h-5 text-teal-300" /></div>
        <h2 className="font-display font-bold text-xl leading-tight" data-testid="cibil-title">{title}</h2>
      </div>
      <p className="text-white/75 max-w-xl leading-relaxed" data-testid="cibil-desc">{desc}</p>
      <div className="mt-5">
        {link ? (
          <a href={link} target="_blank" rel="noreferrer" data-testid="cibil-check-link">
            <Button className="bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-full px-7 h-12">
              {btnLabel} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </a>
        ) : (
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2.5 rounded-full text-sm text-white/70">
            <ShieldCheck className="w-4 h-4 text-teal-300" /> Link will be available shortly.
          </div>
        )}
      </div>
    </div>
  );
}

export default function FinancialTools() {
  useSEO({ title: "Free Financial Calculators", path: "/tools", description: "Free SIP, lumpsum and EMI calculators from Credit Kendra. Plan your investments and loan repayments in seconds.", keywords: "SIP calculator, EMI calculator, lumpsum calculator, loan calculator India" });
  const [cta, setCta] = useState({});
  useEffect(() => { getPublicSettings().then(setCta).catch(() => {}); }, []);
  return (
    <div className="max-w-[1100px] mx-auto px-6 py-14 min-h-[70vh] space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center"><Calculator className="w-6 h-6" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-[#0B223D]">Financial Tools</h1>
          <p className="text-slate-500">Plan smarter with EMI, SIP and credit-score tools.</p>
        </div>
      </div>
      <CibilCheck />
      <div className="grid lg:grid-cols-2 gap-6">
        <EMICalculator cta={cta} />
        <SIPCalculator cta={cta} />
      </div>
    </div>
  );
}
